import { Product, Variant } from "@/types/types";

const OUTLET_CATEGORY_ID = "300000010197";

/**
 * Determines if a variant is express checkout eligible.
 * Express checkout includes Ship From Store (DFS) and Outlet products.
 */
export function isExpressCheckoutVariant(
  variant: Variant,
  product: Product,
): boolean {
  const isShipFromStore = variant.availability?.shipFromStore === true;
  const isOutlet =
    product.browsePageHierarchy?.some(
      (page) => page.categoryId === OUTLET_CATEGORY_ID,
    ) ?? false;

  return isShipFromStore || isOutlet;
}

/**
 * Determines if delivery cards should be suppressed based on variant express checkout state.
 *
 * Suppression logic:
 * - Single SKU product: Suppress if that SKU is express checkout
 * - Multi SKU product, no selection: Don't suppress (show all delivery options)
 * - Multi SKU product, selection: Suppress only if selected variant is express checkout
 */
export function shouldSuppressDeliveryCards(
  product: Product,
  variant: Variant,
  isVariantSelected: boolean,
): boolean {
  const variantCount = product.variants?.length ?? 0;

  // Single SKU: suppress if it's express checkout
  if (variantCount === 1) {
    return isExpressCheckoutVariant(variant, product);
  }

  // Multi SKU, no selection: don't suppress
  if (!isVariantSelected) {
    return false;
  }

  // Multi SKU, selection: suppress if selected variant is express checkout
  return isExpressCheckoutVariant(variant, product);
}
